import DataTable from 'components/DataTable';
import { FinanceType } from '@upstart/harmony/lib/legos/constants';
import { DeskingOffer } from '@upstart/harmony/lib/legos/deskingTypes';
import { startCase } from 'lodash';
import React, { FunctionComponent, useEffect, useState } from 'react';
import Modal from 'react-modal';
import CloseIcon from '../../Icons/Close';
import Input from '../../PencilEditor/Input';
import './ApplyToOtherOffersModal.scss';

const MODAL_STYLE = {
  content: {
    margin: '0 auto',
    maxWidth: '600px',
    position: 'relative',
    padding: 40,
  },
};

interface Props {
  offers: DeskingOffer[];
  isOpen: boolean;
  closeModal: () => void;
  onChange: (offerIds: string[]) => void;
}

const ApplyToOtherOffersModal: FunctionComponent<Props> = props => {
  const { closeModal, isOpen, offers, onChange } = props;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(offers.map(offer => offer.id)),
  );

  useEffect(() => {
    setSelectedIds(new Set(offers.map(offer => offer.id)));
  }, [offers]);

  const onSave = () => {
    onChange(Array.from(selectedIds));
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={MODAL_STYLE as any}
    >
      <CloseIcon onClick={closeModal} />
      <div className="apply-to-offers-modal-header">
        <div className="apply-to-offers-modal-title">Apply to Other Offers</div>
        <div className="apply-to-offers-modal-subtitle">
          Apply changes to the following offers:
        </div>
      </div>
      <div className="apply-to-offers-list">
        <DataTable
          className="apply-to-offers-modal-table"
          emptyMessage="No offers available"
          headers={['', 'Purchase Type', 'Term', 'Notes']}
          rows={offers.map(offer => [
            <Input
              type="checkbox"
              checked={selectedIds.has(offer.id)}
              className="apply-to-offers-list-check"
              onChange={() => {
                if (selectedIds.has(offer.id)) {
                  selectedIds.delete(offer.id);
                } else {
                  selectedIds.add(offer.id);
                }
                setSelectedIds(new Set(selectedIds));
              }}
            />,
            formatFinanceType(offer.data?.financeType),
            formatFinanceTypeUnit(offer),
            formatOfferNote(offer),
          ])}
        />
      </div>
      <div className="apply-to-offers-modal-footer">
        <div className="left-buttons">
          <button
            className="btn btn-primary"
            disabled={offers.length === 0}
            onClick={() =>
              setSelectedIds(new Set(offers.map(offer => offer.id)))
            }
          >
            Select All
          </button>
          <button
            className="btn"
            disabled={offers.length === 0}
            onClick={() => setSelectedIds(new Set())}
          >
            Clear Selection
          </button>
        </div>
        <div>
          <button
            className="btn btn-primary"
            disabled={offers.length === 0 || selectedIds.size === 0}
            onClick={onSave}
          >
            Apply and Save
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ApplyToOtherOffersModal;

function formatFinanceType(type?: FinanceType) {
  switch (type) {
    case FinanceType.PURCHASE:
      return 'Finance';
    case FinanceType.LEASE:
      return 'Lease';
    case FinanceType.CASH:
      return 'Cash';
  }
  return type;
}

function formatFinanceTypeUnit(offer: DeskingOffer) {
  switch (offer.data?.financeType) {
    case FinanceType.PURCHASE:
      return `${offer.data.months} Months`;
    case FinanceType.LEASE:
      return `${offer.data.months} Months | ${offer.data.mileage} Miles`;
  }
  return 'N/A';
}

function formatOfferNote(offer: DeskingOffer) {
  switch (offer.data?.financeType) {
    case FinanceType.PURCHASE:
      return startCase(offer?.data?.category?.type ?? '');
    default:
      break;
  }
  return '';
}
